// 개발요청 — 회차 관리(D5b · AX). 회차(만들기·고치기·상태 전이·삭제 D8) · 참여 부서(다우 조직 묶기·활성) · AX 검토자.
// 🔴 막는 것은 서버다(쓰기는 require_ax · 검토자 지정은 require_sysadmin) — 이 화면은 서버 문구를 그대로 보인다.
// 🔴 회차 상태별 단추 = 서버 routers/devreq.py _TRANSITIONS(draft→open · open→review · review→open · review→closed).
// 🔴 종료는 close-preview 의 message(남는 N건은 다음 회차를 열 때 옮겨집니다)로 확인을 받은 뒤 confirm=true.
//    종료할 때는 아무것도 옮기지 않는다 — 옮기는 때는 다음 회차의 «열기»(draft→open)다.
// 🔴 쓰기가 성공하면 onChanged() — 부모가 me·rounds·departments 를 다시 읽는다(목록·현황판의 회차 고르기 · «+ 새 요청»).
// 🔴 /me 에는 role 이 없다 → 시스템 관리자 판정은 localStorage 의 user.role === 'admin'(검토자 추가·빼기 단추만).
// 🔴 AI P1c — 회차 창 «AI 선별»·«보강 기한»: ai_screen·supplement_due 는 바뀔 때만 싣는다(새 회차는 늘 · 꺼짐→켜짐은 기한도 늘).
//    저장 전 검사의 «오늘»은 기한을 실을 때만 — 기한이 지난 회차의 제목 고치기를 막지 않는다(서버에는 «오늘» 검사가 없다).
// 🔴 AI P2b — «AI 판단 기준»(GET·PUT /api/devreq/ai-criteria · AX 검토자 전원): 글자 수 = 앞뒤 공백 걷은 코드 포인트(서버 len) ·
//    저장은 PUT 응답(GET 과 같은 꼴)을 그대로 쓴다(다시 GET 하지 않는다) · «기본 기준으로» = 빈 글 저장 · 이 묶음엔 role="status"·"alert" 없음.
import { useEffect, useRef, useState } from 'react';
import { devreqAPI } from '../../api/client';
import { confirmDialog } from '../../components/AppDialog';
import { COLORS, RADIUS, btn, card, input as inputStyle } from '../../utils/uiStyles';
import { fmtKstDateTime } from '../../utils/kstTime';
import { errorText, fmtDay, roundName, roundStatusBadge, roundStatusLabel, text } from './devreqLabels';

// 회차 상태 → 단추(서버 _TRANSITIONS 와 같다 · closed 는 끝).
export const ROUND_ACTIONS = {
  draft: [{ to: 'open', label: '열기' }],
  open: [{ to: 'review', label: '접수 마감' }],
  review: [{ to: 'open', label: '다시 열기' }, { to: 'closed', label: '종료' }],
  closed: [],
};
export const NOTHING_TO_CARRY = '넘길 남은 요청이 없습니다. 종료할까요?';
// = 서버 _SHORT_CODE_RE · 400 문구.
const SHORT_CODE_RE = /^[A-Za-z0-9가-힣]{1,10}$/;
export const SHORT_CODE_HINT = '한글·영문·숫자 1~10자 · 하이픈·공백은 안 됩니다 · 요청번호에 들어갑니다(예: 물류 → 2-물류-07)';
export const SHORT_CODE_BAD = '부서 코드는 한글·영문·숫자 1~10자입니다(하이픈·공백 불가).';
// 조직 이름에서 코드 규칙(SHORT_CODE_RE: 한글·영문·숫자 1~10자)에 맞는 글자만 앞 10자.
export function codeFromOrgName(orgName) {
  return String(orgName || '').replace(/[^A-Za-z0-9가-힣]/g, '').slice(0, 10);
}
const DEPT_NAME_BAD = '부서 이름은 1~50자입니다.';
const ROUND_CAP_BAD = '부서당 건수 상한은 1~50 사이의 정수입니다.';
const DEPT_CAP_BAD = '부서 건수 상한은 1~50 사이의 정수입니다.';
const SORT_BAD = '순서는 0~9999 사이의 정수입니다.';
const ROUND_TITLE_MAX = 100;
export const VISIBILITY_HINT = '이 회차에서 새로 내는 요청이 이 값으로 정해져요. 요청 하나만 바꾸려면 상세의 공개 전환 단추를 쓰세요.';
const VISIBILITY_LABELS = { public: '공개', private: '비공개' };
const STAFF_SYSADMIN_ONLY = '검토자 추가·빼기는 시스템 관리자만 할 수 있습니다.';

const th = { padding: '9px', textAlign: 'left', fontSize: '12px', color: COLORS.textSub, whiteSpace: 'nowrap', borderBottom: `2px solid ${COLORS.border}` };
const td = { padding: '8px 9px', fontSize: '13px', borderBottom: `1px solid ${COLORS.borderSoft}`, whiteSpace: 'nowrap', verticalAlign: 'middle' };
const labelStyle = { display: 'block', marginBottom: '4px', color: COLORS.text, fontSize: '12px', fontWeight: 700 };
const noteStyle = { marginTop: '4px', color: COLORS.textSub, fontSize: '12px' };
const fieldStyle = { ...inputStyle, fontSize: '13px', padding: '7px 10px' };
const boxStyle = { marginBottom: '12px', minWidth: 0 };
const pill = (background, color) => ({
  display: 'inline-block', padding: '1px 7px', borderRadius: '999px', fontSize: '11px', fontWeight: 700, background, color, whiteSpace: 'nowrap',
});
const CHIP = {
  display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 4px 2px 9px', borderRadius: '999px', fontSize: '12px',
  background: COLORS.primarySoft, color: COLORS.primaryDark, maxWidth: '100%', overflowWrap: 'anywhere',
};

// 서버 detail 이 글자면 그대로 · 객체({ message })면 그 message · 아니면 대체 문구.
async function failText(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (detail && typeof detail === 'object' && typeof detail.message === 'string' && detail.message) return detail.message;
  return errorText(err, fallback);
}

function readSysadmin() {
  try {
    return JSON.parse(localStorage.getItem('user') || '{}')?.role === 'admin';
  } catch {
    return false;
  }
}

// 빈 칸 → null · 0 이상 정수 → 수 · 그 밖 → NaN.
function intOrNull(value) {
  const raw = String(value ?? '').trim();
  if (raw === '') return null;
  return /^\d+$/.test(raw) ? Number(raw) : NaN;
}

// UTC naive ISO → KST 날짜(YYYY-MM-DD).
function kstDay(value) {
  if (typeof value !== 'string' || !value) return '—';
  const at = new Date(/(Z|[+-]\d\d:?\d\d)$/.test(value) ? value : `${value}Z`);
  if (Number.isNaN(at.getTime())) return '—';
  return new Date(at.getTime() + 9 * 3600 * 1000).toISOString().slice(0, 10);
}

// 오늘(KST · YYYY-MM-DD).
export function todayKst() {
  return new Date(Date.now() + 9 * 3600 * 1000).toISOString().slice(0, 10);
}

// 'YYYY-MM-DD' + n일 — UTC 자정으로 읽어 더한다(지역 시각 new Date(y,m,d) + toISOString 은 KST 에서 하루 당겨진다).
function addDays(day, n) {
  const at = new Date(`${day}T00:00:00Z`);
  if (Number.isNaN(at.getTime())) return '';
  return new Date(at.getTime() + n * 24 * 3600 * 1000).toISOString().slice(0, 10);
}

// 보강 기한 미리 채움 = max(접수 끝, 오늘) + 7일 · 접수 끝이 비면 채우지 않는다('').
export function supplementPrefill(intakeEnd, today) {
  if (!intakeEnd) return '';
  return addDays(intakeEnd > today ? intakeEnd : today, 7);
}

// AI 선별 저장 전 화면 검사 문구 — ①② = 서버 _check_ai_screen 400 문구 · ③ 이틀 간격(마감 다음 날 AI 가 읽고 물을 시간) · ④ 오늘.
export const AI_SCREEN_NEED_END = 'AI 선별을 켜려면 접수 끝 날짜가 필요합니다.';
export const AI_SCREEN_NEED_DUE = 'AI 선별을 켜려면 보강 기한을 접수 끝 뒤 날짜로 정해 주세요.';
export const AI_SCREEN_DUE_GAP = '보강 기한은 접수 끝보다 이틀 이상 뒤로 정해 주세요. 마감 다음 날 AI 가 읽고 답할 시간을 줍니다. 접수 끝을 옮겼다면 보강 기한도 함께 옮겨 주세요.';
export const AI_SCREEN_DUE_PAST = '보강 기한은 오늘보다 뒤 날짜로 정해 주세요.';
export const AI_SCREEN_LABEL = 'AI 선별(마감 뒤 AI 가 먼저 읽고 미흡하면 보강 요청 · 기한까지 답이 없으면 자동 반려)';
export const SUPPLEMENT_HINT = '이 날까지 답이 없으면 그 요청은 자동 반려됩니다. 접수 끝보다 이틀 이상 뒤, 오늘보다 뒤여야 합니다.';
const DUE_MIN_GAP_DAYS = 2;

// 날짜 칸 값 — 빈 값·깨진 값은 ''(서버 null 과 같은 값으로 본다).
const dayOrEmpty = (value) => (fmtDay(value) === '—' ? '' : fmtDay(value));
// 'YYYY-MM-DD' → «M/D».
const monthDay = (value) => {
  const m = /^\d{4}-(\d{2})-(\d{2})/.exec(value || '');
  return m ? `${Number(m[1])}/${Number(m[2])}` : '—';
};

const period = (r) => (r.intake_start || r.intake_end ? `${fmtDay(r.intake_start)} ~ ${fmtDay(r.intake_end)}` : '—');

function useAlive() {
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);
  return aliveRef;
}

function Alert({ children }) {
  return (
    <div role="alert" style={{ marginBottom: '10px', padding: '8px 12px', borderRadius: RADIUS.md, background: COLORS.dangerSoft, color: COLORS.dangerText, fontSize: '13px', overflowWrap: 'anywhere' }}>
      {children}
    </div>
  );
}

function Modal({ label, busy, onClose, children }) {
  const requestClose = () => { if (!busy) onClose?.(); };
  return (
    <div
      role="presentation"
      onMouseDown={(event) => { if (event.target === event.currentTarget) requestClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center',
        justifyContent: 'center', padding: '20px', background: 'rgba(17,24,39,.45)',
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={label}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            requestClose();
          }
        }}
        style={{
          width: 'min(560px, 100%)', maxHeight: '90vh', overflowY: 'auto', boxSizing: 'border-box',
          padding: '22px', borderRadius: RADIUS.lg, background: COLORS.surface, boxShadow: '0 18px 48px rgba(17,24,39,.2)',
        }}
      >
        <h2 style={{ margin: '0 0 14px', color: COLORS.text, fontSize: '18px' }}>{label}</h2>
        {children}
      </div>
    </div>
  );
}

function DialogButtons({ busy, onClose, onSave }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
      <button type="button" onClick={onClose} disabled={busy} style={btn('secondary')}>취소</button>
      <button type="button" onClick={onSave} disabled={busy} style={{ ...btn('primary'), opacity: busy ? 0.5 : 1 }}>{busy ? '저장 중…' : '저장'}</button>
    </div>
  );
}

// ── 회차 ───────────────────────────────────────────────────────────────────
function RoundDialog({ round, onClose, onSaved }) {
  const editing = !!round;
  const initialEnd = dayOrEmpty(round?.intake_end);
  const initialDue = dayOrEmpty(round?.supplement_due);
  const [title, setTitle] = useState(round?.title || '');
  const [start, setStart] = useState(dayOrEmpty(round?.intake_start));
  const [end, setEnd] = useState(initialEnd);
  const [cap, setCap] = useState(String(round?.per_dept_cap ?? 10));
  // 기본 «공개» — AX 가 공개 여부를 정하는 곳은 이 칸(회차 기본값)과 상세의 공개 전환 단추뿐이다.
  const [visibility, setVisibility] = useState(round?.default_visibility === 'private' ? 'private' : 'public');
  // 접수 끝 다음 날 자동 마감(AI P1a) — 새 회차는 켜진 채 · 고치기는 그 회차 값(서버 기본은 꺼짐).
  const [autoClose, setAutoClose] = useState(editing ? !!round?.auto_close : true);
  // AI 선별(AI P1c · 서버 P1b) — 새 회차는 켜진 채(마스터 «최대한 AI 가 먼저») · 고치기는 그 회차 값(서버 기본은 꺼짐).
  const [aiScreen, setAiScreen] = useState(editing ? !!round?.ai_screen : true);
  const [supplement, setSupplement] = useState(initialDue);
  // 이 창이 미리 채운 값 — 칸이 이 값 그대로면 사람이 안 고친 것이라 계기마다 다시 채운다(고치기 창의 원래 값은 여기 없다).
  const [prefilled, setPrefilled] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const aliveRef = useAlive();

  // 미리 채움 계기 둘(접수 끝 바뀜 · AI 켬) — 칸이 비었거나 이 창이 채운 값 그대로일 때만.
  const refill = (endValue) => {
    if (supplement !== '' && supplement !== prefilled) return;
    const next = supplementPrefill(endValue, todayKst());
    if (!next) return;
    setSupplement(next);
    setPrefilled(next);
  };
  const changeEnd = (value) => {
    setEnd(value);
    if (aiScreen) refill(value);
  };
  const changeAiScreen = (checked) => {
    setAiScreen(checked);
    if (checked) refill(end);
  };

  const save = async () => {
    if (savingRef.current) return;
    const capValue = intOrNull(cap);
    if (capValue === null || Number.isNaN(capValue) || capValue < 1 || capValue > 50) {
      setError(ROUND_CAP_BAD);
      return;
    }
    // AI 선별 — 바뀔 때만 싣는다(다른 AX 가 끈 회차를 옛 창이 다시 켜지 않게) · 새 회차는 늘.
    const turningOn = editing && !round.ai_screen && aiScreen;
    const sendAi = !editing || aiScreen !== Boolean(round.ai_screen);
    // 보강 기한 — 켜져 있을 때만 · 새 회차 · 꺼짐→켜짐(같은 값이어도) · 값이 바뀜(빈 칸 = null).
    const sendDue = aiScreen && (!editing || turningOn || supplement !== initialDue);
    // 접수 끝만 옮겨도 서버는 기존 보강 기한으로 다시 본다 → 간격(③)은 창 안의 값으로 함께 본다.
    const endMoved = editing && end !== initialEnd;
    if (aiScreen) {
      // 순서 고정 ①→②→③→④ · 첫 실패 하나만 · 오늘(④)은 보강 기한을 실을 때만(기한 지난 회차의 제목 고치기를 막지 않는다).
      const checkGap = sendDue || endMoved;
      let problem = '';
      if (!end) problem = AI_SCREEN_NEED_END;
      else if (checkGap && !supplement) problem = AI_SCREEN_NEED_DUE;
      else if (checkGap && supplement < addDays(end, DUE_MIN_GAP_DAYS)) problem = AI_SCREEN_DUE_GAP;
      else if (sendDue && supplement <= todayKst()) problem = AI_SCREEN_DUE_PAST;
      if (problem) {
        setError(problem);
        return;
      }
    }
    const body = {
      title: title.trim() || null,
      intake_start: start || null,
      intake_end: end || null,
      per_dept_cap: capValue,
      default_visibility: visibility,
      auto_close: autoClose,
    };
    if (sendAi) body.ai_screen = aiScreen;
    if (sendDue) body.supplement_due = supplement;
    savingRef.current = true;
    setSaving(true);
    setError('');
    try {
      if (editing) await devreqAPI.updateRound(round.id, body);
      else await devreqAPI.createRound(body);
      if (aliveRef.current) onSaved?.();
    } catch (err) {
      const message = await failText(err, '회차를 저장하지 못했습니다.');
      if (aliveRef.current) setError(message);
    } finally {
      savingRef.current = false;
      if (aliveRef.current) setSaving(false);
    }
  };

  return (
    <Modal label={editing ? `회차 고치기 · ${roundName(round)}` : '새 회차'} busy={saving} onClose={onClose}>
      {error && <Alert>{error}</Alert>}
      <div style={boxStyle}>
        <label htmlFor="devreq-round-title" style={labelStyle}>제목</label>
        <input id="devreq-round-title" type="text" value={title} maxLength={ROUND_TITLE_MAX} placeholder="비워도 됩니다(예: 2026 하반기)"
          onChange={(e) => setTitle(e.target.value)} style={fieldStyle} />
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
        <div style={{ ...boxStyle, flex: '1 1 160px' }}>
          <label htmlFor="devreq-round-start" style={labelStyle}>접수 시작</label>
          <input id="devreq-round-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} style={fieldStyle} />
        </div>
        <div style={{ ...boxStyle, flex: '1 1 160px' }}>
          <label htmlFor="devreq-round-end" style={labelStyle}>접수 끝</label>
          <input id="devreq-round-end" type="date" value={end} onChange={(e) => changeEnd(e.target.value)} style={fieldStyle} />
        </div>
      </div>
      <div style={boxStyle}>
        <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
          <input id="devreq-round-autoclose" type="checkbox" checked={autoClose} onChange={(e) => setAutoClose(e.target.checked)} />
          접수 끝 다음 날 자동 마감
        </label>
        <div style={noteStyle}>켜면 «접수 끝» 날짜가 지난 다음 날 0시(한국 시각) 무렵 회차가 저절로 마감됩니다. «접수 끝»이 비어 있으면 마감되지 않습니다.</div>
      </div>
      <div style={boxStyle}>
        <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
          <input id="devreq-round-aiscreen" type="checkbox" checked={aiScreen} onChange={(e) => changeAiScreen(e.target.checked)} />
          {AI_SCREEN_LABEL}
        </label>
        {aiScreen && (
          <div style={{ marginTop: '8px' }}>
            <label htmlFor="devreq-round-supplement" style={labelStyle}>보강 기한</label>
            <input id="devreq-round-supplement" type="date" value={supplement} onChange={(e) => setSupplement(e.target.value)} style={fieldStyle} />
            <div style={noteStyle}>{SUPPLEMENT_HINT}</div>
          </div>
        )}
      </div>
      <div style={boxStyle}>
        <label htmlFor="devreq-round-cap" style={labelStyle}>부서당 건수 상한</label>
        <input id="devreq-round-cap" type="number" min={1} max={50} step={1} value={cap}
          onChange={(e) => setCap(e.target.value)} style={fieldStyle} />
        <div style={noteStyle}>1~50 · 부서마다 따로 정한 상한이 있으면 그 값이 먼저입니다.</div>
      </div>
      <div style={boxStyle}>
        <label htmlFor="devreq-round-visibility" style={labelStyle}>기본 공개 범위</label>
        <select id="devreq-round-visibility" value={visibility} onChange={(e) => setVisibility(e.target.value)} style={fieldStyle}>
          <option value="public">공개</option>
          <option value="private">비공개</option>
        </select>
        <div style={noteStyle}>{VISIBILITY_HINT}</div>
      </div>
      <DialogButtons busy={saving} onClose={onClose} onSave={save} />
    </Modal>
  );
}

function transitionQuestion(round, to) {
  const name = roundName(round);
  if (round.status === 'draft' && to === 'open') return `${name} — 접수를 엽니다. 앞 회차에서 남은 요청이 있으면 이 회차로 옮겨집니다.`;
  if (to === 'review') return `${name} — 접수를 마감합니다. 부서 작성자는 더 이상 요청을 내거나 고칠 수 없습니다.`;
  return `${name} — 접수를 다시 엽니다.`;
}

function RoundsSection({ isMobile, onChanged }) {
  const [rows, setRows] = useState(null);   // null = 불러오는 중
  const [loadError, setLoadError] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState(null);   // null | { round: null | 회차 }
  const busyRef = useRef(false);
  const aliveRef = useAlive();

  const load = () => devreqAPI.rounds()
    .then((r) => {
      if (!aliveRef.current) return;
      setRows(Array.isArray(r?.data) ? r.data : []);
      setLoadError('');
    })
    .catch(async (err) => {
      const message = await failText(err, '회차 목록을 불러오지 못했습니다.');
      if (aliveRef.current) setLoadError(message);
    });

  useEffect(() => { load(); }, []);

  const afterWrite = async () => {
    await load();
    onChanged?.();
  };

  const move = async (round, action) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError('');
    try {
      let question;
      if (action.to === 'closed') {
        // 남는 건을 먼저 보여 준다 — 0건이면 서버 message 가 null 이다.
        const preview = await devreqAPI.closePreview(round.id);
        const message = preview?.data?.message;
        question = typeof message === 'string' && message ? message : NOTHING_TO_CARRY;
      } else {
        question = transitionQuestion(round, action.to);
      }
      if (!(await confirmDialog(question))) return;
      await devreqAPI.transitionRound(round.id, action.to, action.to === 'closed');
      if (!aliveRef.current) return;
      await afterWrite();
    } catch (err) {
      const message = await failText(err, '회차 상태를 바꾸지 못했습니다.');
      if (aliveRef.current) setError(message);
    } finally {
      busyRef.current = false;
      if (aliveRef.current) setBusy(false);
    }
  };

  // 🔴 요청 수(철회 제외)로 단추를 숨기거나 막지 않는다 — 철회한 요청이 있어도 서버가 409(ROUND_HAS_REQUESTS)로 거부한다.
  const remove = async (round) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError('');
    try {
      if (!(await confirmDialog(`${roundName(round)} — 회차를 지웁니다. 되돌릴 수 없습니다.`))) return;
      await devreqAPI.deleteRound(round.id);
      if (!aliveRef.current) return;
      await afterWrite();
    } catch (err) {
      const message = await failText(err, '회차를 지우지 못했습니다.');
      if (aliveRef.current) setError(message);
    } finally {
      busyRef.current = false;
      if (aliveRef.current) setBusy(false);
    }
  };

  const onSaved = () => {
    setDialog(null);
    afterWrite();
  };

  const actions = (r) => (
    <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: '4px' }}>
      <button type="button" aria-label={`${roundName(r)} 고치기`} onClick={() => setDialog({ round: r })} disabled={busy}
        style={btn('secondary', 'sm')}>고치기</button>
      {(ROUND_ACTIONS[r.status] || []).map((action) => (
        <button key={action.to} type="button" aria-label={`${roundName(r)} ${action.label}`} onClick={() => move(r, action)} disabled={busy}
          style={btn(action.to === 'closed' ? 'danger' : 'accent', 'sm')}>{action.label}</button>
      ))}
      <button type="button" aria-label={`${roundName(r)} 삭제`} onClick={() => remove(r)} disabled={busy}
        style={btn('danger', 'sm')}>삭제</button>
    </span>
  );
  const visibilityOf = (r) => VISIBILITY_LABELS[r.default_visibility] || text(r.default_visibility);
  // 상태 옆 작은 배지(켜진 것만) — 열을 늘리지 않는다(상태 칸 안).
  const roundFlags = (r) => [
    r.auto_close === true && <span key="auto" style={pill('#f3f4f6', COLORS.textSub)}>자동 마감</span>,
    r.ai_screen === true && <span key="ai" style={pill(COLORS.infoSoft, COLORS.info)}>{`AI 선별 · 보강 ${monthDay(r.supplement_due)}`}</span>,
  ].filter(Boolean);
  const list = rows || [];

  return (
    <section aria-label="회차" style={{ ...card, marginBottom: '14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: '16px' }}>회차</h2>
        <button type="button" onClick={() => setDialog({ round: null })} disabled={busy} style={{ ...btn('primary', 'sm'), marginLeft: 'auto' }}>새 회차</button>
      </div>
      {loadError && <Alert>{loadError}</Alert>}
      {error && <Alert>{error}</Alert>}
      {!loadError && rows === null && <div role="status" style={{ color: COLORS.textMuted, fontSize: '13px' }}>불러오는 중…</div>}
      {rows && rows.length === 0 && <div style={{ color: COLORS.textMuted, fontSize: '13px' }}>아직 회차가 없습니다 — «새 회차» 로 만드세요</div>}

      {list.length > 0 && (isMobile ? (
        <ul aria-label="회차 목록" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {list.map((r) => (
            <li key={r.id} data-testid="devreq-round-row" style={{ padding: '10px 0', borderBottom: `1px solid ${COLORS.borderSoft}`, fontSize: '13px', minWidth: 0 }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontWeight: 700, overflowWrap: 'anywhere' }}>{roundName(r)}</span>
                <span style={roundStatusBadge(r.status)}>{roundStatusLabel(r.status)}</span>
                {roundFlags(r)}
              </div>
              <div style={{ marginTop: '4px', color: COLORS.textSub, display: 'flex', flexWrap: 'wrap', gap: '2px 10px' }}>
                <span>{`기간 ${period(r)}`}</span>
                <span>{`상한 ${text(r.per_dept_cap)}`}</span>
                <span>{`기본 ${visibilityOf(r)}`}</span>
                <span>{`요청 ${text(r.request_count)}건`}</span>
              </div>
              <div style={{ marginTop: '6px' }}>{actions(r)}</div>
            </li>
          ))}
        </ul>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table aria-label="회차 목록" style={{ width: '100%', borderCollapse: 'collapse', minWidth: '760px' }}>
            <thead>
              <tr>{['번호', '제목', '상태', '기간', '상한', '기본 공개', '요청 수', ''].map((h, i) => <th key={h || i} style={th}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {list.map((r) => (
                <tr key={r.id} data-testid="devreq-round-row">
                  <td style={td}>{`${text(r.round_no)}회차`}</td>
                  <td style={{ ...td, whiteSpace: 'normal', minWidth: '140px' }}>{text(r.title)}</td>
                  <td style={td}>
                    <span style={roundStatusBadge(r.status)}>{roundStatusLabel(r.status)}</span>
                    {roundFlags(r).map((flag) => <span key={flag.key}> {flag}</span>)}
                  </td>
                  <td style={td}>{period(r)}</td>
                  <td style={td}>{text(r.per_dept_cap)}</td>
                  <td style={td}>{visibilityOf(r)}</td>
                  <td style={td}>{text(r.request_count)}</td>
                  <td style={td}>{actions(r)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {dialog && (
        <RoundDialog key={dialog.round ? `edit-${dialog.round.id}` : 'create'} round={dialog.round}
          onClose={() => setDialog(null)} onSaved={onSaved} />
      )}
    </section>
  );
}

// ── 참여 부서 ──────────────────────────────────────────────────────────────
function DeptDialog({ dept, onClose, onSaved }) {
  const editing = !!dept;
  const [name, setName] = useState(dept?.name || '');
  const [code, setCode] = useState(dept?.short_code || '');
  const [orgs, setOrgs] = useState(() => (Array.isArray(dept?.daou_orgs) ? [...dept.daou_orgs] : []));
  const [cap, setCap] = useState(dept?.cap_override === null || dept?.cap_override === undefined ? '' : String(dept.cap_override));
  const [order, setOrder] = useState(dept?.sort_order === null || dept?.sort_order === undefined ? '' : String(dept.sort_order));
  const [q, setQ] = useState('');
  const [found, setFound] = useState(null);   // null = 아직 안 찾았다
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const aliveRef = useAlive();

  const search = async () => {
    if (searching) return;
    setSearching(true);
    setError('');
    try {
      const r = await devreqAPI.daouOrgs(q.trim());
      if (aliveRef.current) setFound(Array.isArray(r?.data) ? r.data : []);
    } catch (err) {
      const message = await failText(err, '다우 조직을 찾지 못했습니다.');
      if (aliveRef.current) setError(message);
    } finally {
      if (aliveRef.current) setSearching(false);
    }
  };
  const addOrg = (orgName) => setOrgs((prev) => (prev.includes(orgName) ? prev : [...prev, orgName]));
  // 조직을 고르면 비어 있는 이름·코드 칸만 채운다(이미 적은 값은 덮지 않는다).
  const pickOrg = (o) => {
    addOrg(o.name);
    if (!name.trim()) setName(String(o.name || '').slice(0, 50));
    if (!code.trim()) setCode(codeFromOrgName(o.name));
  };
  const removeOrg = (orgName) => setOrgs((prev) => prev.filter((o) => o !== orgName));

  const save = async () => {
    if (savingRef.current) return;
    const nameValue = name.trim();
    const codeValue = code.trim();
    const capValue = intOrNull(cap);
    const orderValue = intOrNull(order);
    // 저장 전에 서버와 같은 규칙으로 안내한다(막는 것은 서버다).
    if (nameValue.length < 1 || nameValue.length > 50) { setError(DEPT_NAME_BAD); return; }
    if (!SHORT_CODE_RE.test(codeValue)) { setError(SHORT_CODE_BAD); return; }
    if (capValue !== null && (Number.isNaN(capValue) || capValue < 1 || capValue > 50)) { setError(DEPT_CAP_BAD); return; }
    if (orderValue !== null && (Number.isNaN(orderValue) || orderValue > 9999)) { setError(SORT_BAD); return; }
    let body;
    if (editing) {
      body = {};
      if (nameValue !== dept.name) body.name = nameValue;
      if (codeValue !== dept.short_code) body.short_code = codeValue;
      if (JSON.stringify(orgs) !== JSON.stringify(dept.daou_orgs || [])) body.daou_orgs = orgs;
      if (capValue !== (dept.cap_override ?? null)) body.cap_override = capValue;
      if (orderValue !== null && orderValue !== dept.sort_order) body.sort_order = orderValue;
      if (Object.keys(body).length === 0) { onClose?.(); return; }
    } else {
      // 🔴 daou_orgs 는 필수 칸(서버 DepartmentCreate) — 묶을 조직이 없어도 빈 배열을 보낸다.
      body = { name: nameValue, short_code: codeValue, daou_orgs: orgs };
      if (capValue !== null) body.cap_override = capValue;
      if (orderValue !== null) body.sort_order = orderValue;
    }
    savingRef.current = true;
    setSaving(true);
    setError('');
    try {
      if (editing) await devreqAPI.updateDept(dept.id, body);
      else await devreqAPI.createDept(body);
      if (aliveRef.current) onSaved?.();
    } catch (err) {
      const message = await failText(err, '참여 부서를 저장하지 못했습니다.');
      if (aliveRef.current) setError(message);
    } finally {
      savingRef.current = false;
      if (aliveRef.current) setSaving(false);
    }
  };

  return (
    <Modal label={editing ? `참여 부서 고치기 · ${dept.name}` : '참여 부서 추가'} busy={saving} onClose={onClose}>
      {error && <Alert>{error}</Alert>}
      <div style={boxStyle}>
        <label htmlFor="devreq-dept-name" style={labelStyle}>이름</label>
        <input id="devreq-dept-name" type="text" value={name} maxLength={50} onChange={(e) => setName(e.target.value)} style={fieldStyle} />
      </div>
      <div style={boxStyle}>
        <label htmlFor="devreq-dept-code" style={labelStyle}>코드</label>
        <input id="devreq-dept-code" type="text" value={code} onChange={(e) => setCode(e.target.value)} style={fieldStyle} />
        <div style={noteStyle}>{SHORT_CODE_HINT}</div>
      </div>
      <div style={boxStyle}>
        <div style={labelStyle}>다우 조직</div>
        {orgs.length === 0 ? (
          <div style={{ ...noteStyle, marginTop: 0, marginBottom: '6px' }}>묶은 조직이 없습니다 — 이 부서의 작성자가 없습니다</div>
        ) : (
          <ul aria-label="묶은 다우 조직" style={{ listStyle: 'none', margin: '0 0 6px', padding: 0, display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            {orgs.map((o) => (
              <li key={o} style={CHIP}>
                <span>{o}</span>
                <button type="button" aria-label={`${o} 빼기`} onClick={() => removeOrg(o)}
                  style={{ border: 'none', background: 'transparent', color: COLORS.primaryDark, cursor: 'pointer', fontSize: '13px', padding: '0 4px' }}>×</button>
              </li>
            ))}
          </ul>
        )}
        <div style={{ display: 'flex', gap: '6px' }}>
          <input type="search" aria-label="다우 조직 찾기" placeholder="조직 이름" value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); search(); } }}
            style={fieldStyle} />
          <button type="button" onClick={search} disabled={searching} style={btn('secondary', 'sm')}>찾기</button>
        </div>
        {found !== null && found.length === 0 && <div style={noteStyle}>찾은 조직이 없습니다</div>}
        {found !== null && found.length > 0 && (
          <ul aria-label="찾은 다우 조직" style={{ listStyle: 'none', margin: '6px 0 0', padding: 0, maxHeight: '180px', overflowY: 'auto' }}>
            {found.map((o, i) => (
              <li key={`${o.name}-${o.parent_name || ''}-${i}`}>
                <button type="button" onClick={() => pickOrg(o)} disabled={!!o.dup || orgs.includes(o.name)}
                  style={{ ...btn('secondary', 'sm'), width: '100%', textAlign: 'left', marginBottom: '4px', whiteSpace: 'normal', overflowWrap: 'anywhere', opacity: o.dup ? 0.6 : 1 }}>
                  {`${o.parent_name ? `${o.parent_name} > ` : ''}${text(o.name)}`}
                  {o.dup && ' — 같은 이름 부서가 여럿이라 묶을 수 없습니다'}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
        <div style={{ ...boxStyle, flex: '1 1 160px' }}>
          <label htmlFor="devreq-dept-cap" style={labelStyle}>부서 건수 상한</label>
          <input id="devreq-dept-cap" type="number" min={1} max={50} step={1} value={cap} placeholder="회차 상한"
            onChange={(e) => setCap(e.target.value)} style={fieldStyle} />
          <div style={noteStyle}>비우면 회차의 부서당 상한을 씁니다.</div>
        </div>
        <div style={{ ...boxStyle, flex: '1 1 160px' }}>
          <label htmlFor="devreq-dept-order" style={labelStyle}>순서</label>
          <input id="devreq-dept-order" type="number" min={0} max={9999} step={1} value={order} placeholder="0"
            onChange={(e) => setOrder(e.target.value)} style={fieldStyle} />
        </div>
      </div>
      <DialogButtons busy={saving} onClose={onClose} onSave={save} />
    </Modal>
  );
}

function DepartmentsSection({ isMobile, onChanged }) {
  const [rows, setRows] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState(null);   // null | { dept: null | 부서 }
  const busyRef = useRef(false);
  const aliveRef = useAlive();

  // 🔴 비활성 부서까지(?all=1 · 서버는 AX 에게만) — 꺼 둔 부서를 다시 켤 수 있게.
  const load = () => devreqAPI.departmentsAll()
    .then((r) => {
      if (!aliveRef.current) return;
      setRows(Array.isArray(r?.data) ? r.data : []);
      setLoadError('');
    })
    .catch(async (err) => {
      const message = await failText(err, '참여 부서를 불러오지 못했습니다.');
      if (aliveRef.current) setLoadError(message);
    });

  useEffect(() => { load(); }, []);

  const afterWrite = async () => {
    await load();
    onChanged?.();
  };

  const toggleActive = async (dept) => {
    if (busyRef.current) return;
    const next = !dept.active;
    if (!next && !(await confirmDialog(`${dept.name} 부서를 끕니다. 이 부서 작성자는 새 요청을 낼 수 없습니다.`))) return;
    busyRef.current = true;
    setBusy(true);
    setError('');
    try {
      await devreqAPI.updateDept(dept.id, { active: next });
      if (aliveRef.current) await afterWrite();
    } catch (err) {
      const message = await failText(err, '참여 부서를 바꾸지 못했습니다.');
      if (aliveRef.current) setError(message);
    } finally {
      busyRef.current = false;
      if (aliveRef.current) setBusy(false);
    }
  };

  const onSaved = () => {
    setDialog(null);
    afterWrite();
  };

  const list = rows || [];
  const orgsText = (d) => (Array.isArray(d.daou_orgs) && d.daou_orgs.length > 0 ? d.daou_orgs.join(', ') : '—');
  const activeBox = (d) => (
    <label style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px' }}>
      <input type="checkbox" aria-label={`${d.name} 활성`} checked={!!d.active} disabled={busy} onChange={() => toggleActive(d)} />
      {d.active ? '활성' : '꺼짐'}
    </label>
  );
  const editButton = (d) => (
    <button type="button" aria-label={`${d.name} 고치기`} onClick={() => setDialog({ dept: d })} disabled={busy} style={btn('secondary', 'sm')}>고치기</button>
  );
  const inactiveBadge = (d) => (d.active ? null : <> <span style={pill('#f3f4f6', COLORS.textSub)}>비활성</span></>);

  return (
    <section aria-label="참여 부서" style={{ ...card, marginBottom: '14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: '16px' }}>참여 부서</h2>
        <button type="button" onClick={() => setDialog({ dept: null })} disabled={busy} style={{ ...btn('primary', 'sm'), marginLeft: 'auto' }}>부서 추가</button>
      </div>
      {loadError && <Alert>{loadError}</Alert>}
      {error && <Alert>{error}</Alert>}
      {!loadError && rows === null && <div role="status" style={{ color: COLORS.textMuted, fontSize: '13px' }}>불러오는 중…</div>}
      {rows && rows.length === 0 && <div style={{ color: COLORS.textMuted, fontSize: '13px' }}>참여 부서가 없습니다</div>}

      {list.length > 0 && (isMobile ? (
        <ul aria-label="참여 부서 목록" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {list.map((d) => (
            <li key={d.id} data-testid="devreq-dept-row" style={{ padding: '10px 0', borderBottom: `1px solid ${COLORS.borderSoft}`, fontSize: '13px', minWidth: 0, color: d.active ? COLORS.text : COLORS.textSub }}>
              <div style={{ fontWeight: 700, overflowWrap: 'anywhere' }}>{`${d.name} (${text(d.short_code)})`}{inactiveBadge(d)}</div>
              <div style={{ marginTop: '4px', color: COLORS.textSub, overflowWrap: 'anywhere' }}>{`다우 조직 ${orgsText(d)} · 상한 ${text(d.cap_override)}`}</div>
              <div style={{ marginTop: '6px', display: 'flex', alignItems: 'center', gap: '8px' }}>{activeBox(d)}{editButton(d)}</div>
            </li>
          ))}
        </ul>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table aria-label="참여 부서 목록" style={{ width: '100%', borderCollapse: 'collapse', minWidth: '640px' }}>
            <thead>
              <tr>{['이름', '코드', '다우 조직', '상한', '활성', ''].map((h, i) => <th key={h || i} style={th}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {list.map((d) => (
                <tr key={d.id} data-testid="devreq-dept-row" style={{ color: d.active ? COLORS.text : COLORS.textSub }}>
                  <td style={td}>{d.name}{inactiveBadge(d)}</td>
                  <td style={td}>{text(d.short_code)}</td>
                  <td style={{ ...td, whiteSpace: 'normal', minWidth: '160px', overflowWrap: 'anywhere' }}>{orgsText(d)}</td>
                  <td style={td}>{text(d.cap_override)}</td>
                  <td style={td}>{activeBox(d)}</td>
                  <td style={td}>{editButton(d)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {dialog && (
        <DeptDialog key={dialog.dept ? `edit-${dialog.dept.id}` : 'create'} dept={dialog.dept}
          onClose={() => setDialog(null)} onSaved={onSaved} />
      )}
    </section>
  );
}

// ── AX 검토자 ──────────────────────────────────────────────────────────────
function StaffSection({ onChanged }) {
  const [sysadmin] = useState(readSysadmin);
  const [rows, setRows] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [q, setQ] = useState('');
  const [found, setFound] = useState(null);
  const [searching, setSearching] = useState(false);
  const busyRef = useRef(false);
  const aliveRef = useAlive();

  const load = () => devreqAPI.staff()
    .then((r) => {
      if (!aliveRef.current) return;
      setRows(Array.isArray(r?.data) ? r.data : []);
      setLoadError('');
    })
    .catch(async (err) => {
      const message = await failText(err, '검토자 목록을 불러오지 못했습니다.');
      if (aliveRef.current) setLoadError(message);
    });

  useEffect(() => { load(); }, []);

  const closeAdd = () => { setAdding(false); setQ(''); setFound(null); };

  const search = async () => {
    const term = q.trim();
    if (!term || searching) return;
    setSearching(true);
    setError('');
    try {
      const r = await devreqAPI.searchUsers(term);
      if (aliveRef.current) setFound(Array.isArray(r?.data) ? r.data : []);
    } catch (err) {
      const message = await failText(err, '사용자를 찾지 못했습니다.');
      if (aliveRef.current) setError(message);
    } finally {
      if (aliveRef.current) setSearching(false);
    }
  };

  // 400(활성·승인 아님 · 메뉴 권한 없음) 문구는 서버 그대로 보인다.
  const setStaff = async (userId, staff, after) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError('');
    try {
      await devreqAPI.setStaff(userId, staff);
      if (!aliveRef.current) return;
      after?.();
      await load();
      onChanged?.();
    } catch (err) {
      const message = await failText(err, '검토자를 바꾸지 못했습니다.');
      if (aliveRef.current) setError(message);
    } finally {
      busyRef.current = false;
      if (aliveRef.current) setBusy(false);
    }
  };

  const remove = async (row) => {
    if (busyRef.current) return;
    if (!(await confirmDialog(`${text(row.name)} 님을 검토자에서 뺍니다.`))) return;
    setStaff(row.user_id, false);
  };

  const staffIds = new Set((rows || []).map((r) => r.user_id));

  return (
    <section aria-label="AX 검토자" style={card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: '16px' }}>AX 검토자</h2>
        {sysadmin && !adding && (
          <button type="button" onClick={() => setAdding(true)} disabled={busy} style={{ ...btn('primary', 'sm'), marginLeft: 'auto' }}>검토자 추가</button>
        )}
      </div>
      <div style={{ ...noteStyle, marginTop: 0, marginBottom: '8px' }}>
        시스템 관리자는 따로 지정하지 않아도 검토자입니다.{sysadmin ? '' : ` ${STAFF_SYSADMIN_ONLY}`}
      </div>
      {loadError && <Alert>{loadError}</Alert>}
      {error && <Alert>{error}</Alert>}

      {sysadmin && adding && (
        <div style={{ marginBottom: '10px' }}>
          <div style={{ display: 'flex', gap: '6px' }}>
            <input type="search" aria-label="검토자로 추가할 사용자 찾기" placeholder="이름·이메일" value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); search(); } }}
              style={fieldStyle} />
            <button type="button" onClick={search} disabled={!q.trim() || searching} style={btn('secondary', 'sm')}>찾기</button>
            <button type="button" onClick={closeAdd} style={btn('secondary', 'sm')}>닫기</button>
          </div>
          {found !== null && found.length === 0 && <div style={noteStyle}>찾은 사용자가 없습니다</div>}
          {found !== null && found.length > 0 && (
            <ul aria-label="찾은 사용자" style={{ listStyle: 'none', margin: '6px 0 0', padding: 0 }}>
              {found.map((u) => (
                <li key={u.id} style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px', fontSize: '13px', minWidth: 0 }}>
                  <span style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{`${text(u.name)} · ${text(u.email)}`}</span>
                  {staffIds.has(u.id) ? (
                    <span style={pill(COLORS.successSoft, COLORS.successText)}>검토자</span>
                  ) : (
                    <button type="button" aria-label={`${text(u.name)} 추가`} onClick={() => setStaff(u.id, true, closeAdd)} disabled={busy}
                      style={btn('accent', 'sm')}>추가</button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {!loadError && rows === null && <div role="status" style={{ color: COLORS.textMuted, fontSize: '13px' }}>불러오는 중…</div>}
      {rows && rows.length === 0 && <div style={{ color: COLORS.textMuted, fontSize: '13px' }}>지정한 검토자가 없습니다</div>}
      {rows && rows.length > 0 && (
        <ul aria-label="검토자 목록" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {rows.map((r) => (
            <li key={r.user_id} data-testid="devreq-staff-row"
              style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px 10px', padding: '8px 0', borderBottom: `1px solid ${COLORS.borderSoft}`, fontSize: '13px', minWidth: 0 }}>
              <span style={{ fontWeight: 700 }}>{text(r.name)}</span>
              <span style={{ color: COLORS.textSub, overflowWrap: 'anywhere' }}>{text(r.email)}</span>
              <span style={{ color: COLORS.textSub }}>{`지정 ${kstDay(r.granted_at)}`}</span>
              {sysadmin && (
                <button type="button" aria-label={`${text(r.name)} 빼기`} onClick={() => remove(r)} disabled={busy}
                  style={{ ...btn('danger', 'sm'), marginLeft: 'auto' }}>빼기</button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ── AI 판단 기준(AI P2b · 서버 P2a · AX 검토자 전원 — 마스터 #3638 (나)) ─────────────────────────
export const CRITERIA_MAX = 4000;   // = 서버 devreq_ai_report.CRITERIA_MAX
export const CRITERIA_TITLE = 'AI 판단 기준';
export const CRITERIA_NOTE = '1차 판독을 통과한 요청마다 AI 가 이 기준으로 종합 판단을 만들어요. 결정은 사람이 해요.';
export const CRITERIA_DEFAULT_BADGE = '기본 기준';
export const CRITERIA_RESET_LABEL = '기본 기준으로';
export const CRITERIA_RESET_CONFIRM = 'AI 판단 기준을 기본 기준으로 되돌릴까요?';
export const CRITERIA_SAVED = '저장했어요';
export const CRITERIA_TOO_LONG = '4,000자를 넘어 저장할 수 없어요.';
export const CRITERIA_FORBIDDEN = 'AI 판단 기준은 AX 검토자만 볼 수 있어요';
export const CRITERIA_RESET_LINE = '기본 기준으로 되돌림';
export const CRITERIA_NO_HISTORY = '아직 고친 적이 없어요';
const CRITERIA_UNKNOWN_ACTOR = '알 수 없음';
const CRITERIA_LOAD_FAILED = 'AI 판단 기준을 불러오지 못했습니다.';
const CRITERIA_SAVE_FAILED = 'AI 판단 기준을 저장하지 못했습니다.';
const CRITERIA_HISTORY_MAX = 10;   // = 서버 HISTORY_LIMIT
const CRITERIA_EXCERPT = 200;      // = 서버 CRITERIA_EVENT_MAX
// 오류 글 — 🔴 role="alert" 를 쓰지 않는다(이 묶음엔 status·alert 없음 · 위 세 묶음의 Alert 단정과 섞이지 않게).
const failStyle = { padding: '8px 12px', borderRadius: RADIUS.md, background: COLORS.dangerSoft, color: COLORS.dangerText, fontSize: '13px', overflowWrap: 'anywhere' };

// 글자 수 = 앞뒤 공백을 걷은 코드 포인트 수(서버 len(text.strip()) 과 같다 — 이모지 하나 = 1).
export const criteriaLength = (value) => [...String(value ?? '').trim()].length;
const thousands = (n) => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

// 응답 → 화면 값(글자 아닌 값은 빈 글 · 이력은 서버 순서(새것부터) 그대로 10줄까지).
function pickCriteria(data) {
  const d = data && typeof data === 'object' ? data : {};
  return {
    criteria: typeof d.criteria === 'string' ? d.criteria : '',
    isDefault: d.is_default === true,
    defaultText: typeof d.default_text === 'string' ? d.default_text : '',
    updatedAt: typeof d.updated_at === 'string' ? d.updated_at : null,
    history: (Array.isArray(d.history) ? d.history : []).filter((h) => h && typeof h === 'object').slice(0, CRITERIA_HISTORY_MAX),
  };
}
const isCriteriaView = (data) => !!data && typeof data === 'object' && typeof data.criteria === 'string';

// 이력 한 줄 — «바꾼 사람 · 시각(KST) · 글 앞 200자»(빈 글 = 기본 기준으로 되돌림).
export function criteriaHistoryLine(h) {
  const who = typeof h?.actor_name === 'string' && h.actor_name.trim() ? h.actor_name : CRITERIA_UNKNOWN_ACTOR;
  const when = fmtKstDateTime(h?.at) || '—';
  const body = typeof h?.text === 'string' && h.text.trim() ? [...h.text].slice(0, CRITERIA_EXCERPT).join('') : CRITERIA_RESET_LINE;
  return `${who} · ${when} · ${body}`;
}

// 400 = 서버 detail 그대로 · 403 = AX 검토자만 · 그 밖 = errorText.
async function criteriaFail(err, fallback) {
  if (err?.response?.status === 403) return CRITERIA_FORBIDDEN;
  return errorText(err, fallback);
}

// 바깥(훅 없음) — 목에 aiCriteria 가 없으면(기존 시험) 아무것도 그리지 않는다.
function CriteriaSection() {
  if (typeof devreqAPI?.aiCriteria !== 'function') return null;
  return <CriteriaEditor />;
}

function CriteriaEditor() {
  const [view, setView] = useState(null);   // null = 불러오는 중 · pickCriteria 값
  const [draft, setDraft] = useState('');
  const [loadError, setLoadError] = useState('');
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const aliveRef = useAlive();

  const apply = (data) => {
    const next = pickCriteria(data);
    setView(next);
    setDraft(next.criteria);
  };

  // 부르는 중 던져도 사슬 안이라 catch 로 간다.
  const load = () => Promise.resolve()
    .then(() => devreqAPI.aiCriteria())
    .then((r) => {
      if (!aliveRef.current) return;
      apply(r?.data);
      setLoadError('');
    })
    .catch(async (err) => {
      const message = await criteriaFail(err, CRITERIA_LOAD_FAILED);
      if (aliveRef.current) setLoadError(message);
    });

  useEffect(() => { load(); }, []);

  const save = async (value) => {
    if (busyRef.current || typeof devreqAPI.saveAiCriteria !== 'function') return;
    busyRef.current = true;
    setBusy(true);
    setError('');
    setSaved(false);
    try {
      const r = await devreqAPI.saveAiCriteria(value);
      if (!aliveRef.current) return;
      // PUT 응답은 GET 과 같은 꼴 — 그대로 쓴다(꼴이 아닐 때만 다시 읽는다).
      if (isCriteriaView(r?.data)) apply(r.data);
      else await load();
      if (aliveRef.current) setSaved(true);
    } catch (err) {
      const message = await criteriaFail(err, CRITERIA_SAVE_FAILED);
      if (aliveRef.current) setError(message);
    } finally {
      busyRef.current = false;
      if (aliveRef.current) setBusy(false);
    }
  };

  // 기본 기준으로 = 빈 글 저장(서버가 빈 글을 기본 기준으로 친다).
  const reset = async () => {
    if (busyRef.current) return;
    if (!(await confirmDialog(CRITERIA_RESET_CONFIRM))) return;
    await save('');
  };

  const length = criteriaLength(draft);
  const tooLong = length > CRITERIA_MAX;
  const trimmed = draft.trim();
  // 바뀐 게 없음 = 앞뒤 공백 걷은 값끼리 같다 · 기본 기준을 보는 중에 기본 글(또는 빈 글) 그대로면 끈다(기본 글이 저장값으로 굳지 않게).
  const unchanged = view !== null && trimmed === view.criteria.trim();
  const sameAsDefault = view !== null && view.isDefault && (trimmed === '' || trimmed === view.defaultText.trim());
  const canSave = view !== null && !busy && !tooLong && !unchanged && !sameAsDefault;
  const canReset = view !== null && !busy && !view.isDefault;
  const history = view ? view.history : [];

  return (
    <section aria-label={CRITERIA_TITLE} style={{ ...card, marginTop: '14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: '16px' }}>{CRITERIA_TITLE}</h2>
        {view?.isDefault && <span style={pill(COLORS.infoSoft, COLORS.info)}>{CRITERIA_DEFAULT_BADGE}</span>}
        {view?.updatedAt && (
          <span style={{ fontSize: '12px', color: COLORS.textSub }}>{`마지막 수정 ${fmtKstDateTime(view.updatedAt) || '—'}`}</span>
        )}
      </div>
      <div style={{ ...noteStyle, marginTop: 0, marginBottom: '8px' }}>{CRITERIA_NOTE}</div>
      {loadError && <div style={failStyle}>{loadError}</div>}
      {!loadError && view === null && <div style={{ color: COLORS.textMuted, fontSize: '13px' }}>불러오는 중…</div>}
      {view !== null && (
        <>
          <textarea aria-label="AI 판단 기준 글" value={draft} rows={8}
            onChange={(e) => { setDraft(e.target.value); setSaved(false); }}
            style={{ ...fieldStyle, resize: 'vertical', lineHeight: 1.5 }} />
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px', marginTop: '6px' }}>
            <span data-testid="devreq-criteria-count" style={{ fontSize: '12px', color: tooLong ? COLORS.dangerText : COLORS.textSub, fontWeight: tooLong ? 700 : 400 }}>
              {`${thousands(length)} / ${thousands(CRITERIA_MAX)}`}
            </span>
            {tooLong && <span style={{ fontSize: '12px', color: COLORS.dangerText }}>{CRITERIA_TOO_LONG}</span>}
            {saved && <span style={{ fontSize: '12px', color: COLORS.successText }}>{CRITERIA_SAVED}</span>}
            <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: '6px' }}>
              <button type="button" onClick={reset} disabled={!canReset}
                style={{ ...btn('secondary', 'sm'), opacity: canReset ? 1 : 0.5 }}>{CRITERIA_RESET_LABEL}</button>
              <button type="button" onClick={() => save(trimmed)} disabled={!canSave}
                style={{ ...btn('primary', 'sm'), opacity: canSave ? 1 : 0.5 }}>저장</button>
            </span>
          </div>
          {error && <div style={{ ...failStyle, marginTop: '8px' }}>{error}</div>}
          <div style={{ ...labelStyle, marginTop: '12px' }}>최근 수정 이력</div>
          {history.length === 0 ? (
            <div style={{ color: COLORS.textMuted, fontSize: '13px' }}>{CRITERIA_NO_HISTORY}</div>
          ) : (
            <ul aria-label="AI 판단 기준 이력" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {/* 이력에는 id 가 없다 — 서버가 새것부터 고정 순서로 주므로 순번을 키에 섞는다. */}
              {history.map((h, i) => (
                <li key={`${h.at || ''}-${i}`}
                  style={{ padding: '6px 0', borderBottom: `1px solid ${COLORS.borderSoft}`, fontSize: '12px', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                  {criteriaHistoryLine(h)}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}

export default function DevRequestAdmin({ isMobile = false, onChanged }) {
  return (
    <div aria-label="회차 관리" role="region">
      <RoundsSection isMobile={isMobile} onChanged={onChanged} />
      <DepartmentsSection isMobile={isMobile} onChanged={onChanged} />
      <StaffSection onChanged={onChanged} />
      <CriteriaSection />
    </div>
  );
}
